import React, { useState, useEffect, useRef } from 'react';
import {
  Typography,
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  TextField,
  Button,
  Avatar,
  CircularProgress,
  IconButton,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Grow,
  Slider
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Area,
  AreaChart
} from 'recharts';
import CheckIcon from '@mui/icons-material/Check';
import ChatIcon from '@mui/icons-material/Chat';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GroupIcon from '@mui/icons-material/Group';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SendIcon from '@mui/icons-material/Send';
import SettingsIcon from '@mui/icons-material/Settings';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import ParametricControls from '../../components/common/ParametricControls';
import AnimatedNumber from '../../components/common/AnimatedNumber';

const Chatbot = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [demoMode, setDemoMode] = useState('visitor');
  const [userMessage, setUserMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([
    { 
      sender: 'bot', 
      message: 'Merhaba! Ben D-CAT Analytics akıllı asistanı. Size nasıl yardımcı olabilirim?',
      timestamp: new Date().toISOString() 
    },
  ]);
  const [isTyping, setIsTyping] = useState(false);
  const [params, setParams] = useState({
    sector: 'retail',
    language: 'turkish',
    model: 'advanced',
    confidenceThreshold: 75
  });

  const chatEndRef = useRef(null);

  // Scrolls to the bottom of the chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleDemoModeChange = (event) => {
    setDemoMode(event.target.value);
  };

  const handleSubmitMessage = () => {
    if (!userMessage.trim()) return;

    // Add user message to chat history
    const newUserMessage = {
      sender: 'user',
      message: userMessage,
      timestamp: new Date().toISOString()
    };
    
    setChatHistory(prev => [...prev, newUserMessage]);
    setUserMessage('');
    setIsTyping(true);

    // Simulate bot response after a short delay
    setTimeout(() => {
      const botResponse = generateBotResponse(userMessage);
      setChatHistory(prev => [...prev, {
        sender: 'bot',
        message: botResponse,
        timestamp: new Date().toISOString()
      }]);
      setIsTyping(false);
    }, 1000 + Math.random() * 1000); // Random delay for realism
  };

  // Simple bot response generator for demo
  const generateBotResponse = (message) => {
    message = message.toLowerCase();
    
    if (message.includes('merhaba') || message.includes('selam')) {
      return 'Merhaba! Size nasıl yardımcı olabilirim?';
    } else if (message.includes('ürün') && message.includes('fiyat')) {
      return 'Ürünlerimizin fiyatları hakkında detaylı bilgi için kataloğumuza göz atabilirsiniz. Size yardımcı olabilecek başka bir konu var mı?';
    } else if (message.includes('sipariş') && message.includes('takip')) {
      return 'Siparişinizi takip etmek için sipariş numaranızı paylaşabilirsiniz. Veya "Hesabım" sayfasından sipariş durumunuzu kontrol edebilirsiniz.';
    } else if (message.includes('iade')) {
      return 'İade işlemleri için satın aldığınız ürünün faturası ile birlikte mağazalarımıza başvurabilirsiniz. Online alışverişlerde ise ürün teslim tarihinden itibaren 14 gün içinde iade talebinde bulunabilirsiniz.';
    } else if (message.includes('teşekkür')) {
      return 'Rica ederim! Başka bir konuda yardıma ihtiyacınız olursa bana yazabilirsiniz.';
    } else if (message.includes('kampanya') || message.includes('indirim')) {
      return 'Şu an devam eden kampanyalarımız: Tüm giyim ürünlerinde %30 indirim, 2 kitap alana 3. kitap hediye, ve elektronik ürünlerde sepette %10 indirim. Daha detaylı bilgi için kampanyalar sayfamızı ziyaret edebilirsiniz.';
    } else {
      return 'Bu konuda size yardımcı olabilirim. Daha fazla bilgi için müşteri temsilcilerimizle görüşmek ister misiniz?';
    }
  };

  const handleParamsChange = (newParams) => {
    setParams(newParams);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmitMessage();
    }
  };

  // Mock data
  const satisfactionData = [
    { month: 'Oca', önceki: 72, chatbot: 78 },
    { month: 'Şub', önceki: 73, chatbot: 82 },
    { month: 'Mar', önceki: 71, chatbot: 85 },
    { month: 'Nis', önceki: 74, chatbot: 87 },
    { month: 'May', önceki: 75, chatbot: 90 },
    { month: 'Haz', önceki: 73, chatbot: 92 },
  ];

  const queryResolutionData = [
    { name: 'İlk Seferde Çözülen', value: 68 },
    { name: 'Ek Bilgi Gerektiren', value: 22 },
    { name: 'İnsan Desteği Gerektiren', value: 10 },
  ];
  
  const COLORS = ['#00C49F', '#FFBB28', '#FF8042'];

  const responseTimeData = [
    { time: '08:00', ortalama: 0.2 },
    { time: '10:00', ortalama: 0.3 },
    { time: '12:00', ortalama: 0.5 },
    { time: '14:00', ortalama: 0.4 },
    { time: '16:00', ortalama: 0.2 },
    { time: '18:00', ortalama: 0.1 },
    { time: '20:00', ortalama: 0.2 },
    { time: '22:00', ortalama: 0.1 },
  ];

  const dailyInteractionsData = [
    { date: '01/06', interactions: 1200 },
    { date: '02/06', interactions: 1380 },
    { date: '03/06', interactions: 1500 },
    { date: '04/06', interactions: 1290 },
    { date: '05/06', interactions: 1430 },
    { date: '06/06', interactions: 1550 },
    { date: '07/06', interactions: 1620 },
  ];

  const benefitsData = [
    'Müşteri hizmetleri maliyetlerinde %30 azalma',
    '7/24 kesintisiz müşteri desteği',
    'Ortalama yanıt süresinde %85 iyileşme',
    'Self-servis çözüm oranında %42 artış',
    'Müşteri memnuniyetinde %18 artış'
  ];

  const chatbotCapabilities = [
    { icon: <ChatIcon />, title: 'Çoklu Dil Desteği', description: '15+ dilde destek' },
    { icon: <AccessTimeIcon />, title: 'Anında Yanıt', description: 'Milisaniyeler içinde cevap' },
    { icon: <GroupIcon />, title: 'Kişiselleştirme', description: 'Müşteri profiline özel yanıtlar' },
    { icon: <TrendingUpIcon />, title: 'Sürekli Öğrenme', description: 'Etkileşimlerden kendini geliştirir' }
  ];

  const languageOptions = [
    { value: 'turkish', label: 'Türkçe' },
    { value: 'english', label: 'İngilizce' },
    { value: 'german', label: 'Almanca' },
    { value: 'french', label: 'Fransızca' },
    { value: 'spanish', label: 'İspanyolca' }
  ];

  const modelOptions = [
    { value: 'basic', label: 'Temel Model' },
    { value: 'standard', label: 'Standart Model' },
    { value: 'advanced', label: 'Gelişmiş Model' },
    { value: 'enterprise', label: 'Kurumsal Model' }
  ];

  const messageTypes = [
    { value: 'product', label: 'Ürün Bilgisi', example: 'X ürününün fiyatı nedir?' },
    { value: 'order', label: 'Sipariş Takibi', example: '12345 numaralı siparişim nerede?' },
    { value: 'return', label: 'İade İşlemi', example: 'Ürün iade etmek istiyorum' },
    { value: 'promo', label: 'Kampanyalar', example: 'Şu anda indirimde olan ürünler var mı?' },
    { value: 'account', label: 'Hesap İşlemleri', example: 'Şifremi unuttum' },
  ];

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }} className="slide-in-left">
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
          Chatbot Çözümleri
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" paragraph>
          Yapay zeka destekli chatbot çözümleri ile müşteri deneyimini geliştirin ve operasyonel maliyetlerinizi düşürün.
        </Typography>
        <Divider sx={{ my: 3 }} />
      </Box>

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{ mb: 3 }}
        className="fade-in"
      >
        <Tab label="Canlı Demo" />
        <Tab label="Performans Analizi" />
        <Tab label="Özellikler ve Faydalar" />
      </Tabs>

      {activeTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper 
              elevation={3} 
              sx={{ 
                height: '70vh', 
                display: 'flex', 
                flexDirection: 'column',
                borderRadius: 3,
                overflow: 'hidden',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '4px',
                  background: 'linear-gradient(90deg, #3f51b5, #f50057)',
                }
              }}
            >
              {/* Chat Header */}
              <Box 
                sx={{ 
                  bgcolor: 'background.paper',
                  p: 2,
                  borderBottom: '1px solid rgba(0,0,0,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: 'primary.main', mr: 1 }}>
                    <SmartToyIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">D-CAT Asistan</Typography>
                    <Typography variant="body2" color="text.secondary">Çevrimiçi</Typography>
                  </Box>
                </Box>
                <IconButton>
                  <SettingsIcon />
                </IconButton>
              </Box>
              
              {/* Chat Messages Area */}
              <Box 
                sx={{ 
                  flexGrow: 1, 
                  p: 2, 
                  overflowY: 'auto',
                  bgcolor: 'background.default',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                {chatHistory.map((chat, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      justifyContent: chat.sender === 'user' ? 'flex-end' : 'flex-start',
                      mb: 2
                    }}
                  >
                    {chat.sender === 'bot' && (
                      <Avatar 
                        sx={{ 
                          bgcolor: 'primary.main', 
                          width: 32, 
                          height: 32, 
                          mr: 1,
                          mt: 0.5
                        }}
                      >
                        <SmartToyIcon fontSize="small" />
                      </Avatar>
                    )}
                    <Box>
                      <Paper
                        sx={{
                          p: 1.5,
                          maxWidth: '80%',
                          borderRadius: chat.sender === 'user' ? '20px 20px 0 20px' : '20px 20px 20px 0',
                          bgcolor: chat.sender === 'user' ? 'primary.light' : 'background.paper',
                          color: chat.sender === 'user' ? 'white' : 'text.primary',
                          boxShadow: 1
                        }}
                      >
                        <Typography variant="body2">{chat.message}</Typography>
                      </Paper>
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        sx={{ display: 'block', mt: 0.5, ml: chat.sender === 'user' ? 'auto' : 0 }}
                      >
                        {new Date(chat.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    </Box>
                    {chat.sender === 'user' && (
                      <Avatar 
                        sx={{ 
                          bgcolor: 'secondary.main', 
                          width: 32, 
                          height: 32, 
                          ml: 1,
                          mt: 0.5
                        }}
                      >
                        <PersonIcon fontSize="small" />
                      </Avatar>
                    )}
                  </Box>
                ))}
                {isTyping && (
                  <Box sx={{ display: 'flex', alignItems: 'center', ml: 7 }}>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    <Typography variant="body2" color="text.secondary">Yazıyor...</Typography>
                  </Box>
                )}
                <div ref={chatEndRef} />
              </Box>
              
              {/* Chat Input Area */}
              <Box 
                sx={{ 
                  p: 2, 
                  borderTop: '1px solid rgba(0,0,0,0.1)',
                  bgcolor: 'background.paper',
                  display: 'flex'
                }}
              >
                <IconButton sx={{ mr: 1 }}>
                  <AttachFileIcon />
                </IconButton>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Mesajınızı yazın..."
                  size="small"
                  value={userMessage}
                  onChange={(e) => setUserMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  multiline
                  maxRows={3}
                />
                <Button 
                  variant="contained"
                  color="primary"
                  endIcon={<SendIcon />}
                  onClick={handleSubmitMessage}
                  sx={{ ml: 1 }}
                  disabled={!userMessage.trim() || isTyping}
                >
                  Gönder
                </Button>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Stack spacing={2}>
              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Demo Modu</Typography>
                  <FormControl fullWidth size="small">
                    <InputLabel id="demo-mode-label">Senaryo</InputLabel>
                    <Select
                      labelId="demo-mode-label"
                      value={demoMode}
                      onChange={handleDemoModeChange}
                      label="Senaryo"
                    >
                      <MenuItem value="visitor">Ziyaretçi</MenuItem>
                      <MenuItem value="customer">Kayıtlı Müşteri</MenuItem>
                      <MenuItem value="returning">Eski Müşteri</MenuItem>
                    </Select>
                  </FormControl>
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Örnek Sorular</Typography>
                  <List dense>
                    {messageTypes.map((type) => (
                      <ListItem 
                        key={type.value} 
                        button 
                        onClick={() => setUserMessage(type.example)}
                        sx={{ borderRadius: 1, '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}
                      >
                        <ListItemText 
                          primary={type.label} 
                          secondary={type.example}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Chatbot Ayarları</Typography>
                  <Box sx={{ mb: 2 }}>
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                      <InputLabel>Dil</InputLabel>
                      <Select
                        name="language"
                        value={params.language}
                        onChange={(e) => setParams({...params, language: e.target.value})}
                        label="Dil"
                      >
                        {languageOptions.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    
                    <FormControl fullWidth size="small">
                      <InputLabel>Model</InputLabel>
                      <Select
                        name="model"
                        value={params.model}
                        onChange={(e) => setParams({...params, model: e.target.value})}
                        label="Model"
                      >
                        {modelOptions.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  <Typography gutterBottom>Güven Eşiği: {params.confidenceThreshold}%</Typography>
                  <Slider
                    value={params.confidenceThreshold}
                    onChange={(e, newValue) => setParams({...params, confidenceThreshold: newValue})}
                    aria-labelledby="confidence-threshold-slider"
                    min={50}
                    max={95}
                    step={5}
                  />
                </CardContent>
              </Card>
            </Stack>
          </Grid>
        </Grid>
      )}

      {activeTab === 1 && (
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <ParametricControls
              title="Performans Parametreleri"
              onParamsChange={handleParamsChange}
              defaultParams={params}
            />
          </Grid>

          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>Müşteri Memnuniyeti Karşılaştırması</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart
                  data={satisfactionData}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis domain={[50, 100]} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="önceki" name="Geleneksel Destek (%)" stroke="#8884d8" />
                  <Line type="monotone" dataKey="chatbot" name="Chatbot ile Destek (%)" stroke="#82ca9d" activeDot={{ r: 8 }} />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Grow in={true} timeout={800}>
              <Paper elevation={2} sx={{ p: 3, height: '100%', borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>Sorgu Çözüm Dağılımı</Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={queryResolutionData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {queryResolutionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Grow>
          </Grid>

          <Grid item xs={12} md={6}>
            <Grow in={true} timeout={1000}>
              <Paper elevation={2} sx={{ p: 3, height: '100%', borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>Ortalama Yanıt Süresi (saniye)</Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={responseTimeData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="time" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="ortalama" name="Ortalama Yanıt Süresi" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grow>
          </Grid>
          
          <Grid item xs={12}>
            <Grow in={true} timeout={1200}>
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>Günlük Etkileşimler</Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart
                    data={dailyInteractionsData}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient id="colorInteractions" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#8884d8" stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Area 
                      type="monotone" 
                      dataKey="interactions" 
                      stroke="#8884d8" 
                      fillOpacity={1} 
                      fill="url(#colorInteractions)" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Paper>
            </Grow>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Başarı Metrikleri</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={4}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <AnimatedNumber 
                        value={68} 
                        suffix="%" 
                        duration={1500} 
                        variant="h4" 
                        color="primary.main"
                        fontWeight="bold"
                      />
                      <Typography variant="body2" color="text.secondary">
                        İlk seferde çözüm oranı
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <AnimatedNumber 
                        value={0.3} 
                        suffix="sn" 
                        duration={1500} 
                        variant="h4" 
                        color="primary.main"
                        fontWeight="bold"
                      />
                      <Typography variant="body2" color="text.secondary">
                        Ortalama yanıt süresi
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <AnimatedNumber 
                        value={92} 
                        suffix="%" 
                        duration={1500} 
                        variant="h4" 
                        color="primary.main"
                        fontWeight="bold"
                      />
                      <Typography variant="body2" color="text.secondary">
                        Müşteri memnuniyeti
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>İş Etkisi</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={4}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <AnimatedNumber 
                        value={30} 
                        suffix="%" 
                        duration={1500} 
                        variant="h4" 
                        color="success.main"
                        fontWeight="bold"
                      />
                      <Typography variant="body2" color="text.secondary">
                        Maliyet Tasarrufu
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <AnimatedNumber 
                        value={42} 
                        suffix="%" 
                        duration={1500} 
                        variant="h4" 
                        color="success.main"
                        fontWeight="bold"
                      />
                      <Typography variant="body2" color="text.secondary">
                        Self-Servis Oranı
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <AnimatedNumber 
                        value={85} 
                        suffix="%" 
                        duration={1500} 
                        variant="h4" 
                        color="success.main"
                        fontWeight="bold"
                      />
                      <Typography variant="body2" color="text.secondary">
                        Yanıt İyileştirmesi
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {activeTab === 2 && (
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Chatbot Özellikleri
                </Typography>
                <List>
                  {chatbotCapabilities.map((capability, index) => (
                    <ListItem key={index} sx={{ py: 1 }}>
                      <ListItemIcon>
                        <Avatar sx={{ bgcolor: 'primary.light', color: 'white' }}>
                          {capability.icon}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText 
                        primary={capability.title}
                        secondary={capability.description}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  İş Faydaları
                </Typography>
                <List>
                  {benefitsData.map((benefit, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <CheckIcon color="success" />
                      </ListItemIcon>
                      <ListItemText primary={benefit} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Teknik Özellikler
                </Typography>
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, bgcolor: 'background.default', height: '100%' }}>
                      <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                        Mimari
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        Chatbot çözümümüz, doğal dil işleme (NLP) ve makine öğrenmesi algoritmalarını 
                        kullanan gelişmiş bir yapay zeka altyapısına sahiptir.
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Modüler yapısı sayesinde mevcut sistemlerinizle kolayca entegre olabilir.
                      </Typography>
                    </Paper>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, bgcolor: 'background.default', height: '100%' }}>
                      <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                        Öğrenme Kapasitesi
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        Her müşteri etkileşiminden öğrenen yapay zeka modeli, zamanla daha doğru ve 
                        kişiselleştirilmiş yanıtlar üretir.
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Düzenli model güncellemeleri ve eğitimleri ile sürekli gelişir.
                      </Typography>
                    </Paper>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, bgcolor: 'background.default', height: '100%' }}>
                      <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                        Entegrasyon Kanalları
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        Web sitesi, mobil uygulama, WhatsApp, Facebook Messenger, Instagram gibi birçok 
                        kanalda hizmet verebilir.
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        CRM, ERP ve diğer iş sistemleriyle API üzerinden entegre olur.
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default Chatbot;
